/* English UI strings. Only the interface is translated — never the data. */
export const en = {
  app: { name: 'ETC Connect', tagline: 'We innovate to shape brilliant futures.' },
  common: {
    save: 'Save', cancel: 'Cancel', edit: 'Edit', search: 'Search',
    loading: 'Loading…', back: 'Back', close: 'Close', confirm: 'Confirm',
    all: 'All', yes: 'Yes', no: 'No', required: 'Required',
    readonly: 'Read-only', editable: 'Editable', blocked: 'Blocked',
    page: 'Page', of: 'of', next: 'Next', prev: 'Previous',
    complete: 'Complete', incomplete: 'Pending', primary: 'Primary',
    active: 'Active', orphan: 'Disassociated', filter: 'Filter',
    view: 'View', open: 'Open', request: 'Request', submit: 'Submit',
    upload: 'Upload', download: 'Download', or: 'or',
    signin: 'Sign in', signout: 'Sign out',
    username: 'Username', password: 'Password', soon: 'Coming soon',
    last_updated: 'Last updated',
    completeness: '% Progress',
    missing: 'Missing',
  },
  nav: {
    dashboard: 'Dashboard',
    org: 'My organization',
    sites: 'Sites',
    instructors: 'Instructors',
    users: 'Users & roles',
    admin: 'Administration',
    orgs: 'Organizations',
    reports: 'Reports',
  },
  login: {
    welcome: 'Welcome to the partner portal',
    sub: 'Learning and Certification partners',
    forgot: 'Forgot your password?',
    forgot_title: 'Recover your password',
    forgot_help: 'Enter the email tied to your account. We will send you a link to reset your password.',
    send_link: 'Send link',
    sent_title: 'Check your email',
    sent_help: 'If an account exists for {email} you will receive a link within a few minutes. The email itself says how long the link lasts.',
    sent_resend: 'Resend link',
    sent_no_email: 'Did not receive it? Check your spam folder. If it does not arrive, contact the ETC Iberoamérica team.',
    back_to_login: 'Back to sign in',
    reset_title: 'Create new password',
    reset_help: 'Choose a strong password. At least 12 characters, one uppercase and one number.',
    new_password: 'New password',
    confirm_password: 'Confirm password',
    password_mismatch: 'Passwords do not match.',
    password_weak: 'At least 8 characters, one uppercase and one number.',
    reset_success: 'Password updated. You can sign in now.',
    pw_show: 'Show',
    pw_hide: 'Hide',
    pw_strength: 'Strength',
    pw_strength_weak: 'Weak',
    pw_strength_med: 'Medium',
    pw_strength_strong: 'Strong',
  },
  roles: {
    ETC_ADMIN: 'ETC Admin',
    PARTNER_MGR: 'Partner Manager / Admin',
    SITE_MGR: 'Site Manager',
    SITE_ADMIN: 'Site Admin',
    ATC_DIR: 'ATC Director',
    INSTRUCTOR: 'Instructor',
  },
  window: {
    open_banner: 'Update process open · Closes {date}',
    closed_banner: 'Update process closed · Read-only mode. Next process: {date}',
    closed_banner_admin: 'Update process closed for partners · You can still edit. Next process: {date}',
    readonly_role: 'Your account is read-only',
    admin_open: 'Open process',
    admin_close: 'Close process',
    status_open: 'Open',
    status_closed: 'Closed',
  },
  kpi: {
    orgs: 'Organizations',
    sites: 'Sites',
    active_instr: 'Active instructors',
    orphans: 'Disassociated',
    red_pending: 'Records with pending reds',
    completeness: '% Progress',
  },
  field: {
    state_editable: 'Editable',
    state_readonly: 'Read-only',
    state_required: 'Required',
    state_blocked: 'Blocked',
    state_tip_edit: 'Partner may modify this field.',
    state_tip_read: 'Shown but not editable.',
    state_tip_req: 'Required. It counts towards your update-process percentage.',
    state_tip_lock: 'System reference or derived. Not editable.',
    legend: 'Field legend',
    required_empty: 'This field is required',
    otro_sin_texto: 'You ticked “Other”: say which one',
    codigo_invalido: 'Value not recognised; use one from the list',
    postal_invalid: 'Invalid postal code (empty or "0000" not allowed)',
    email_invalid: 'Invalid email',
    phone_invalid: 'Invalid phone (must include country code)',
  },
  sec: {
    general: 'General',
    registered_address: 'Registered address',
    contract_address: 'Contractual',
    billing_address: 'Billing',
    billing_contact: 'Billing contact',
    legal_contact: 'Legal contact',
    atc_director: 'ATC Director',
    site_admin: 'Site Admin',
    site_manager: 'Site Manager',
    identifiers: 'Identifiers',
    site_data: 'Site data',
    assignments: 'Affiliated to:',
    personal: 'Personal details',
    perfil: 'Partner profile',
  },
  legal: {
    request_change: 'Request legal contact change',
    readonly_note: 'Legal contact is read-only for all partner roles. Request a change by attaching the official appointment document.',
    status_pending: 'Change requested · under ETC review',
    request_title: 'Request legal contact change',
    request_help: 'Attach the official appointment document (PDF). ETC will review.',
    attach_doc: 'Attach document',
    new_first: 'New first name',
    new_last: 'New last name',
    new_email: 'New email',
    new_phone: 'New phone',
    sent: 'Request sent to ETC',
  },
  admin: {
    title: 'ETC administration',
    window: 'Update process',
    window_help: 'Enables editing for all partners during a defined period.',
    deadline: 'Deadline',
    requests: 'Legal contact change requests',
    approve: 'Approve', reject: 'Reject',
    import: 'Import from Excel',
    import_help: 'Upload an .xlsx file with organizations, sites, instructors or assignments. Preview before applying.',
    choose_file: 'Choose file',
    target_entity: 'Target entity',
    rows_valid: 'Valid rows',
    rows_errors: 'Rows with errors',
    map_columns: 'Column mapping',
    apply: 'Apply to system',
    orphans: 'Disassociated instructors',
    orphans_help: 'Instructors with no active site affiliation. Can be deactivated.',
    deactivate: 'Deactivate',
  },
  sites: {
    title: 'Sites',
    primary_label: 'Primary site',
    primary_tip: 'The primary site cannot be unassigned without reassigning another.',
    change_primary: 'Change primary site',
  },
  instr: {
    title: 'Instructors',
    filter_all: 'All',
    filter_active: 'Active',
    filter_orphan: 'Disassociated',
    filter_by_site: 'By site',
  },
  users: {
    title: 'Users & roles',
    matrix: 'Permission matrix',
    scope: 'Scope',
    can_edit: 'Can edit',
    cannot: 'Cannot',
  },
  chpw: {
    title: 'Choose your password',
    intro: 'Hi {name}. You signed in with a temporary password, so it has to be changed before you continue.',
    current: 'Temporary password',
    next: 'New password',
    repeat: 'Repeat the new password',
    too_short: 'It must be at least 12 characters.',
    mismatch: 'The two passwords do not match.',
    submit: 'Save and continue',
    saving: 'Saving…',
    signout: 'Sign out',
    weak_hint: 'It is long, but mixing in capitals and numbers makes it much harder to guess.',
  },
} as const;
